/**
 * SecurityTab — Failed auth top-IPs, hourly histogram, and MCP errors by code.
 */

import { useState, useEffect, useCallback } from 'react';
import { getAdminSecurity, type SecurityStatsResult } from '../../api/client.js';
import { TrendChart } from './TrendChart.js';

// ============================================================================
// Helpers
// ============================================================================

function formatTimeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
}

// ============================================================================
// Spinner
// ============================================================================

function Spinner({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ============================================================================
// SecurityTab
// ============================================================================

const WINDOW_OPTIONS = [
  { label: '1h', value: 60 },
  { label: '6h', value: 360 },
  { label: '24h', value: 1440 },
  { label: '7d', value: 10080 },
] as const;

type WindowOption = typeof WINDOW_OPTIONS[number]['value'];

export function SecurityTab() {
  const [windowMinutes, setWindowMinutes] = useState<WindowOption>(1440);
  const [data, setData] = useState<SecurityStatsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (w: WindowOption) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminSecurity(w);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load security stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(windowMinutes);
  }, [windowMinutes, fetchData]);

  // Build chart from hourly histogram
  const histogramSeries = data
    ? data.hourlyHistogram.map((e) => ({ time: e.hour, value: e.count }))
    : [];

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 gap-3">
        <Spinner className="w-5 h-5" />
        <span>Loading security stats...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <p className="text-red-400">{error}</p>
        <button
          onClick={() => { void fetchData(windowMinutes); }}
          className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-200 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Disclaimer */}
      <div className="flex items-start gap-2 px-4 py-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
        <svg className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-xs text-yellow-300">
          Failed-auth data is IP-based. API requests are <strong>not</strong> attributed per user
          (no request audit log exists). This data reflects authentication failures only.
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setWindowMinutes(opt.value)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                windowMinutes === opt.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => { void fetchData(windowMinutes); }}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg text-gray-200 transition-colors disabled:opacity-50"
        >
          {loading ? <Spinner /> : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Reload
        </button>
      </div>

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-400 tabular-nums">{data.totalFailedAttempts.toLocaleString()}</div>
              <div className="text-sm text-gray-400 mt-1">Failed Auth Attempts</div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-400 tabular-nums">{data.totalMcpErrors.toLocaleString()}</div>
              <div className="text-sm text-gray-400 mt-1">MCP Errors</div>
            </div>
          </div>

          {/* Hourly histogram */}
          {histogramSeries.length > 0 && (
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-2">
              <h3 className="text-sm font-medium text-white">Failed Auth — Hourly Histogram</h3>
              <TrendChart data={histogramSeries} color="#EF4444" height={150} />
            </div>
          )}

          {/* Top IPs table */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-white">Top Offending IPs</h3>
            {data.topIps.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">No failed auth attempts in this window.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900 border-b border-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">IP</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Attempts</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Reasons</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Attempt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topIps.map((row, i) => (
                      <tr key={row.ip} className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}>
                        <td className="px-3 py-2 text-gray-100 font-mono text-xs">{row.ip}</td>
                        <td className="px-3 py-2 text-red-400 tabular-nums font-medium">{row.attempts.toLocaleString()}</td>
                        <td className="px-3 py-2 text-gray-400 text-xs">
                          {row.reasons.join(', ') || '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-400 whitespace-nowrap text-xs">
                          {formatTimeAgo(row.lastAttempt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* MCP errors by code */}
          {data.mcpErrorsByCode.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-white">MCP Errors by Code</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-900 border-b border-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Error Code</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mcpErrorsByCode.map((row, i) => (
                      <tr key={row.errorCode} className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}>
                        <td className="px-3 py-2 text-yellow-300 font-mono text-xs">{row.errorCode}</td>
                        <td className="px-3 py-2 text-gray-300 tabular-nums">{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
